import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiLoader } = FiIcons;

const Button = forwardRef(function Button({
  as = 'button',
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  className = '',
  ...props
}, ref) {
  const baseClasses =
    'inline-flex items-center justify-center font-dm-sans font-bold rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 touch-manipulation active:scale-[0.98]';

  const variantClasses = {
    primary: 'bg-pine text-paper border-transparent hover:bg-pine/90 focus:ring-pine shadow-sm',
    secondary: 'bg-white text-pine border border-pine/10 hover:bg-pine/5 focus:ring-pine shadow-sm',
    danger: 'bg-crimson text-white border-transparent hover:bg-crimson/90 focus:ring-crimson',
    warning: 'bg-apricot text-pine border-transparent hover:bg-apricot/90 focus:ring-apricot',
    success: 'bg-sage text-pine border-transparent hover:bg-sage/90 focus:ring-sage',
    ghost: 'bg-transparent text-pine hover:bg-pine/5 focus:ring-pine',
    paper: '!bg-paper !text-pine !border-transparent hover:!bg-white focus:ring-paper shadow-sm',
  };

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const isDisabled = disabled || loading;

  const combinedClasses = [
    baseClasses,
    variantClasses[variant] || variantClasses.primary,
    sizeClasses[size],
    isDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
    className,
  ].join(' ');

  const isIntrinsicElement = typeof as === 'string';
  const MotionComponent = isIntrinsicElement ? motion[as] : as;

  const componentProps = { ...props };
  if (as === 'button') {
    componentProps.disabled = isDisabled;
  } else if (as === 'a' && isDisabled) {
    componentProps['aria-disabled'] = true;
    componentProps.onClick = (e) => e.preventDefault();
  } else if (!isIntrinsicElement && isDisabled) {
    componentProps['aria-disabled'] = true;
    componentProps.tabIndex = -1;
    componentProps.onClick = (e) => e.preventDefault();
  }

  return (
    <MotionComponent
      {...(isIntrinsicElement ? { whileTap: { scale: isDisabled ? 1 : 0.98 } } : {})}
      ref={ref}
      className={combinedClasses}
      {...componentProps}
    >
      {loading && (
        <SafeIcon icon={FiLoader} className={`${iconSizeClasses[size]} animate-spin mr-2`} />
      )}
      {!loading && Icon && iconPosition === 'left' && (
        <SafeIcon icon={Icon} className={`${iconSizeClasses[size]} mr-2`} />
      )}
      {children}
      {!loading && Icon && iconPosition === 'right' && (
        <SafeIcon icon={Icon} className={`${iconSizeClasses[size]} ml-2`} />
      )}
    </MotionComponent>
  );
});

export default Button;